import React, { useEffect, useMemo, useState } from "react";

// Firebase
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";
import { formatFirebaseTime } from "../../utils";

// Library
import Avatar from "react-avatar";
import { Bounce, toast } from "react-toastify";

// Custom Component
import PaymentModal from "../../Components/PaymentModal/PaymentModal";
import Table from "../../Components/Table/Table";
import Loader from "../../Components/Loader/Loader";

const InstantRequest = () => {
  const [data, setData] = useState([]);
  const [selectedRide, setSelectedRide] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestsRef = collection(db, COLLECTION_NAME.requestMoney);
        const q = query(requestsRef, where("status", "==", "unpaid"));

        const querySnapshot = await getDocs(q);
        const dataList = querySnapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            ...data,
            createdAt: formatFirebaseTime(data.createdAt) || "-----",
          };
        });

        setData(dataList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data: ", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openModal = (ride) => {
    setSelectedRide(ride);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!processingRequest) {
      setSelectedRide(null);
      setIsModalOpen(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Profile Picture",
        accessor: "profilePicture",
        Cell: ({ row }) => (
          <Avatar
            src={row.original.profilePicture}
            name={row.original.fullName}
            size="40"
            round
          />
        ),
      },
      {
        Header: "Full Name",
        accessor: "fullName",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Amount",
        accessor: "amount",
      },
      {
        Header: "Created At",
        accessor: "createdAt",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              className="text-primary bg-celadon p-1 rounded-md"
              style={{
                height: "30px",
                width: "80px",
                borderRadius: "5px",
              }}
              onClick={() => {
                openModal(row.original);
                setStatus("pay");
              }}
            >
              Pay
            </button>
            <button
              className="bg-red-500 hover:bg-red-700 text-white rounded-md"
              style={{
                height: "30px",
                width: "80px",
                borderRadius: "5px",
              }}
              onClick={() => {
                openModal(row.original);
                setStatus("cancel");
              }}
            >
              Cancel
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const onConfirm = async () => {
    if (!selectedRide || !selectedRide.id) return;

    const rideId = selectedRide.id;
    setProcessingRequest(true);

    try {
      if (status === "cancel") {
        await updateDoc(doc(db, "Request Money", rideId), {
          status: "cancelled",
        });

        toast.success("Request cancelled successfully!", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });

        setData((prevData) => prevData.filter((ride) => ride.id !== rideId));
      } else {
        const userDetails = await getDoc(doc(db, COLLECTION_NAME.users, rideId));

        if (userDetails.exists()) {
          const updateData = {
            ...userDetails.data(),
            totalPaidOff:
              parseFloat(userDetails.data().totalPaidOff || 0) +
              parseFloat(selectedRide.amount),
          };

          await updateDoc(doc(db, COLLECTION_NAME.users, rideId), updateData);

          await updateDoc(doc(db, "Request Money", rideId), {
            status: "paid",
          });

          toast.success("Payment processed successfully!", {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          });

          setData((prevData) => prevData.filter((ride) => ride.id !== rideId));
        } else {
          throw new Error("User document not found");
        }
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setProcessingRequest(false);
      setIsModalOpen(false);
      setSelectedRide(null);
    }
  };

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
        Request Money
      </h1>

      {loading ? (
        <Loader />
      ) : (
        data && (
          <div className="">
            <div className="">
              <Table columns={columns} data={data} screen={"instantMoney"} />
            </div>

            <PaymentModal
              isOpen={isModalOpen}
              onRequestClose={closeModal}
              status={status}
              loading={processingRequest}
              onConfirm={onConfirm}
              ride={selectedRide}
            />
          </div>
        )
      )}
    </div>
  );
};

export default InstantRequest;

